/**
 * One deadline for a piece of network work (CAUSE-B F1, 2026-10-26 prod demo).
 *
 * A request on a dead socket never settles by itself. The deadline aborts the request's signal (so the browser
 * closes the socket and the next attempt opens a fresh one) AND rejects the work at once, even when the work
 * ignores its signal (for example a response body that stalls). The caller's own signal is composed with the
 * deadline, never replaced. The deadline is a setTimeout (not AbortSignal.timeout) so it is cleared as soon as the
 * work settles and fake timers can drive it.
 */
import { abortReason, linkSignals } from './linkSignals'
import { getFetchRequestSignal } from './fetchRequest'

export class DeadlineExceededError extends Error {
  override readonly name = 'TimeoutError'

  constructor(readonly what: string, readonly timeoutMs: number) {
    super(`${what} timed out after ${timeoutMs} ms`)
  }
}

const noop = () => undefined

/** Run `work` under a deadline of `timeoutMs`, composed with the optional `callerSignal`. */
export async function withDeadline<T>(
  work: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  what: string,
  callerSignal?: AbortSignal | null,
): Promise<T> {
  const timeout = new AbortController()
  const timer = setTimeout(() => timeout.abort(new DeadlineExceededError(what, timeoutMs)), timeoutMs)
  const linked = callerSignal ? linkSignals([callerSignal, timeout.signal]) : { signal: timeout.signal, dispose: noop }
  const { signal } = linked

  let onAbort: () => void = noop
  const aborted = new Promise<never>((_resolve, reject) => {
    onAbort = () => reject(abortReason(signal))
    if (signal.aborted) onAbort()
    else signal.addEventListener('abort', onAbort, { once: true })
  })
  aborted.catch(noop)

  let running: Promise<T>
  try {
    running = signal.aborted ? aborted : work(signal)
  } catch (err) {
    running = Promise.reject(err)
  }
  try {
    return await Promise.race([running, aborted])
  } finally {
    clearTimeout(timer)
    signal.removeEventListener('abort', onAbort)
    linked.dispose()
    running.catch(noop) // the loser of the race must not surface as an unhandled rejection
  }
}

/**
 * Bound the whole response, including its body. fetch() alone settles at headers:
 * auth-js then reads JSON while still holding its exclusive auth lock. Drain a
 * clone under the same deadline, leaving the original Response and its metadata
 * intact and unread for Supabase. Only auth/REST use this helper; storage streams
 * remain exempt at the policy call site.
 */
export function fetchWithDeadline(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  timeoutMs: number,
  what: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<Response> {
  return withDeadline(async (signal) => {
    const response = await fetchImpl(input, { ...init, signal })
    await response.clone().arrayBuffer()
    return response
  }, timeoutMs, what, getFetchRequestSignal(input, init))
}
